import type { PluginListenerHandle } from '@capacitor/core'
import { AgentChatSpeech, isNativeSpeechAvailable } from './nativeSpeech'

type VoiceControlOptions = {
  onCommand: () => void
  onError: (message: string) => void
}

type StopVoiceControl = () => Promise<void>
type BrowserRecognition = InstanceType<NonNullable<Window['SpeechRecognition']>>

const SESSION_LIMIT_MS = 120_000
let activeStop: StopVoiceControl | null = null
let generation = 0

export function parseNewCodexVoiceCommand(input: string): boolean {
  const phrase = input.toLowerCase().replace(/[,.!?;:]/g, ' ').replace(/\s+/g, ' ').trim()
  return /^(?:hey agent\s?chat )?(?:please )?(?:(?:start|open|create) (?:a )?)?new codex (?:chat|conversation)(?: please)?$/.test(phrase)
}

export async function stopVoiceControl(): Promise<void> {
  generation += 1
  await activeStop?.()
}

export async function startVoiceControl(options: VoiceControlOptions): Promise<StopVoiceControl> {
  const requestedGeneration = ++generation
  await activeStop?.()
  if (requestedGeneration !== generation) return async () => undefined

  let stopped = false
  let nativeStarted = false
  let browser: BrowserRecognition | null = null
  const listeners: PluginListenerHandle[] = []
  let restartTimer: ReturnType<typeof setTimeout> | undefined
  let sessionTimer: ReturnType<typeof setTimeout> | undefined
  let cleanup: Promise<void> | null = null
  let setup: Promise<void> = Promise.resolve()
  let finishBrowserSetup: (() => void) | null = null

  const stop: StopVoiceControl = () => {
    if (cleanup) return cleanup
    stopped = true
    clearTimeout(restartTimer)
    clearTimeout(sessionTimer)
    finishBrowserSetup?.()
    if (browser) {
      browser.onstart = null
      browser.onresult = null
      browser.onerror = null
      browser.onend = null
    }
    cleanup = (async () => {
      await setup.catch(() => undefined)
      try {
        browser?.abort()
        if (nativeStarted) await AgentChatSpeech.cancel()
      } finally {
        try {
          await Promise.all(listeners.map((listener) => listener.remove()))
        } finally {
          if (activeStop === stop) activeStop = null
        }
      }
    })()
    return cleanup
  }
  activeStop = stop

  function fail(message: string) {
    if (stopped) return
    void stop().then(
      () => options.onError(message),
      () => options.onError(`${message} Microphone cleanup failed.`),
    )
  }

  function accept(transcript: string) {
    if (stopped || !parseNewCodexVoiceCommand(transcript)) return
    void stop().then(options.onCommand, () => options.onError('Could not stop voice control. Tap Stop and try again.'))
  }

  setup = Promise.resolve().then(async () => {
    if (stopped) return
    if (isNativeSpeechAvailable()) {
      const availability = await AgentChatSpeech.isAvailable()
      if (stopped) return
      if (!availability.available) throw new Error('Speech recognition is unavailable on this device.')
      if (availability.permission === 'denied') throw new Error('Allow microphone access for AgentChat, then try again.')
      listeners.push(await AgentChatSpeech.addListener('finalResult', (event) => accept(event.transcript)))
      if (stopped) return
      listeners.push(await AgentChatSpeech.addListener('error', (event) => {
        if (!event.recoverable) fail(event.message || 'Speech recognition failed. Try voice control again.')
      }))
      if (stopped) return
      listeners.push(await AgentChatSpeech.addListener('stateChange', (event) => {
        if (event.state === 'stopped') fail('Speech recognition stopped. Tap voice control to try again.')
      }))
      if (stopped) return
      nativeStarted = true
      await AgentChatSpeech.start({ locale: 'en-IE', partialResults: false, continuous: true })
      return
    }

    const Recognition = window.SpeechRecognition ?? window.webkitSpeechRecognition
    if (!Recognition) throw new Error('Voice control is unavailable in this browser. Try the Android app or Chrome.')
    browser = new Recognition()
    browser.lang = 'en-IE'
    browser.continuous = true
    browser.interimResults = false
    browser.maxAlternatives = 1
    browser.onresult = (event) => {
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index]
        if (result.isFinal) accept(result[0]?.transcript ?? '')
      }
    }
    browser.onerror = (event) => {
      if (event.error === 'no-speech') return
      fail(event.error === 'not-allowed' || event.error === 'service-not-allowed'
        ? 'Allow microphone access for AgentChat, then try again.'
        : 'Speech recognition failed. Check your connection and try again.')
    }
    let restarts = 0
    browser.onend = () => {
      if (stopped) return
      restarts += 1
      if (restarts > 20) {
        fail('Speech recognition stopped. Tap voice control to try again.')
        return
      }
      restartTimer = setTimeout(() => {
        if (stopped) return
        try { browser?.start() } catch { fail('Speech recognition could not restart. Try voice control again.') }
      }, 250)
    }
    const recognition = browser
    await new Promise<void>((resolve) => {
      finishBrowserSetup = resolve
      recognition.onstart = resolve
      recognition.start()
    })
  })

  let setupTimer: ReturnType<typeof setTimeout> | undefined
  const setupDeadline = new Promise<never>((_, reject) => {
    setupTimer = setTimeout(() => reject(new Error('Voice control did not start. Check microphone permission and try again.')), 20_000)
  })
  try {
    await Promise.race([setup, setupDeadline])
    if (stopped) await stop()
    else sessionTimer = setTimeout(() => fail('Voice control stopped after two minutes. Tap it to listen again.'), SESSION_LIMIT_MS)
  } catch (error) {
    void stop().catch(() => options.onError('Could not stop voice control. Try reopening AgentChat.'))
    throw error
  } finally {
    clearTimeout(setupTimer)
  }
  return stop
}
